import { functions, functionsAsia } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { useData } from "@/contexts/DataContext";
import { useToast } from "@/hooks/use-toast";
import { type BusinessProfile } from "@/lib/firebase";
import {
  Loader2,
  Building2,
  User,
  Mail,
  Phone,
  Globe,
  MapPin,
  ChevronRight,
  CheckCircle2,
  BadgeCheck,
  Sparkles,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { motion, AnimatePresence } from "framer-motion";

interface BusinessSetupProps {
  onComplete: () => void;
  initialData?: Partial<BusinessProfile>;
}

type SetupStep =
  | "welcome"
  | "businessName"
  | "businessEmail"
  | "businessPhone"
  | "businessAddress"
  | "businessWebsite"
  | "referral"
  | "completing";

export default function BusinessSetup({
  onComplete,
  initialData,
}: BusinessSetupProps) {
  const { user } = useAuth();
  const { saveBusinessProfile } = useData();
  const { toast } = useToast();
  const [step, setStep] = useState<SetupStep>("welcome");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  // MANDATORY AUTO-FILL LOGIC PRESERVED
  const [formData, setFormData] = useState<BusinessProfile>({
    ownerName: initialData?.ownerName || user?.displayName || "",
    businessName: initialData?.businessName || "",
    businessAddress: initialData?.businessAddress || "",
    businessEmail: initialData?.businessEmail || user?.email || "",
    businessPhone: initialData?.businessPhone || user?.phoneNumber || "+91",
    businessWebsite: initialData?.businessWebsite || "",
  });

  // SILENT MEMORY BRIDGE: Snatch the code from memory or clipboard
  useEffect(() => {
    if (step === "referral") {
      const checkReferralSource = async () => {
        const savedCode = localStorage.getItem('early_referral_grab');
        if (savedCode) {
          setReferralCode(savedCode);
          // 🚨 UPDATE THIS TOAST:
          toast({ title: "Invite Applied!", description: "Your friend's code has been linked." });
          return;
        }
        try {
          const { Clipboard } = await import('@capacitor/clipboard');
          const { value } = await Clipboard.read();
          if (value && /^[A-Z0-9]{8}$/.test(value.trim().toUpperCase())) {
            setReferralCode(value.trim().toUpperCase());
          }
        } catch (e) { }
      };
      checkReferralSource();
    }
  }, [step, toast]);

  // PHONE AUTO-FILL LOGIC PRESERVED
  useEffect(() => {
    if (
      user?.phoneNumber &&
      !initialData?.businessPhone &&
      (formData.businessPhone === "+91" || formData.businessPhone === "")
    ) {
      setFormData((prev) => ({ ...prev, businessPhone: user.phoneNumber! }));
    }
  }, [user?.phoneNumber, initialData?.businessPhone, formData.businessPhone]);

  const nextStep = () => {
    if (step === "welcome") setStep("businessName");
    else if (step === "businessName") {
      const trimmedName = formData.businessName.trim();
      if (!trimmedName) {
        toast({ title: "Business Name is required", variant: "destructive" });
        return;
      }
      setFormData({ ...formData, businessName: trimmedName });
      setStep("businessEmail");
    } else if (step === "businessEmail") {
      if (!formData.businessEmail.trim()) {
        toast({ title: "Business Email is required", variant: "destructive" });
        return;
      }
      setStep("businessPhone");
    } else if (step === "businessPhone") {
      const phoneDigits = formData.businessPhone.startsWith("+91")
        ? formData.businessPhone.slice(3)
        : formData.businessPhone.replace(/\D/g, "");
      if (phoneDigits.length > 0 && phoneDigits.length < 10) {
        toast({ title: "Phone number must be 10 digits", variant: "destructive" });
        return;
      }
      setStep("businessAddress");
    } else if (step === "businessAddress") setStep("businessWebsite");
    else if (step === "businessWebsite") setStep("referral");
    else if (step === "referral") handleFinalSubmit();
  };

  const skipStep = () => {
    if (step === "businessPhone") setStep("businessAddress");
    else if (step === "businessAddress") setStep("businessWebsite");
    else if (step === "businessWebsite") setStep("referral");
    else if (step === "referral") handleFinalSubmit();
  };

  const handleFinalSubmit = async () => {
    if (!user) return;
    setStep("completing");
    setIsSubmitting(true);
    try {
      const cleanWebsite = formData.businessWebsite ? formData.businessWebsite.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "") : "";
      const profile: BusinessProfile = {
        ...formData,
        businessWebsite: cleanWebsite,
        phoneVerified: user?.phoneNumber ? formData.businessPhone === user.phoneNumber : false,
      };

      await saveBusinessProfile(profile);

      // REFERRAL LINKING
      if (referralCode) {
        const { applyReferralCode } = await import("@/lib/firebase");
        await applyReferralCode(referralCode, user.uid, formData.businessName);
        localStorage.removeItem('early_referral_grab');
      }

      const welcomeTasks: Promise<unknown>[] = [];
      if (formData.businessEmail) {
        const sendWelcome = httpsCallable(functionsAsia, 'triggerWelcomeEmail');
        welcomeTasks.push(
          sendWelcome({ toEmail: formData.businessEmail, businessName: formData.businessName, ownerName: formData.ownerName || formData.businessName }).catch(() => { })
        );
      }
      if (formData.businessPhone) {
        const sendWelcomeWA = httpsCallable(functions, 'triggerWelcomeWhatsApp');
        welcomeTasks.push(
          sendWelcomeWA({ phoneNumber: formData.businessPhone, businessName: formData.businessName }).catch(() => { })
        );
      }
      await Promise.race([
        Promise.allSettled(welcomeTasks),
        new Promise((resolve) => setTimeout(resolve, 8000)),
      ]);

      onComplete();
    } catch (error: any) {
      toast({ title: "Failed to save profile", variant: "destructive" });
      setStep("referral");
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepVariants = {
    enter: { x: 50, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -50, opacity: 0 },
  };

  const renderStep = () => {
    switch (step) {
      case "welcome":
        return (
          <motion.div key="welcome" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6 text-center">
            <div className="flex justify-center mb-8"><Logo size="lg" /></div>
            <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-3xl font-bold">Successfully Connected!</h2>
            <p className="text-muted-foreground text-lg">Welcome to DailyHisaab. Let's get your business profile set up in just a few quick steps.</p>
            <Button size="lg" className="w-full h-14 text-lg" onClick={nextStep}>Get Started <ChevronRight className="ml-2 h-5 w-5" /></Button>
          </motion.div>
        );

      case "businessName":
        return (
          <motion.div key="businessName" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
            <div className="flex justify-center mb-4"><Logo size="md" /></div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">What's your business name?</h2>
              <p className="text-muted-foreground">This will appear on your bills and reports.</p>
            </div>
            <div className="space-y-4">
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input autoFocus className="pl-10 h-14 text-lg" placeholder="e.g. Nalhati Grocery" value={formData.businessName} onChange={(e) => setFormData({ ...formData, businessName: e.target.value })} />
              </div>
              <Button size="lg" className="w-full h-14 text-lg" onClick={nextStep}>Next Step</Button>
            </div>
          </motion.div>
        );

      case "businessEmail":
        return (
          <motion.div key="businessEmail" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
            <div className="flex justify-center mb-4"><Logo size="md" /></div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Your email address</h2>
              <p className="text-muted-foreground">Enter your email address.</p>
            </div>
            <div className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input type="email" className="pl-10 h-14 text-lg" placeholder="e.g. nalhati.grocery@example.com" value={formData.businessEmail} onChange={(e) => setFormData({ ...formData, businessEmail: e.target.value })} />
              </div>
              <Button size="lg" className="w-full h-14 text-lg" onClick={nextStep}>Next Step</Button>
            </div>
          </motion.div>
        );

      case "businessPhone":
        return (
          <motion.div key="businessPhone" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
            <div className="flex justify-center mb-4"><Logo size="md" /></div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Add a phone number</h2>
              <p className="text-muted-foreground">Customers can reach you at this number.</p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 border border-border rounded-md bg-muted text-sm text-muted-foreground h-14 shrink-0 select-none">
                  <span className="text-base leading-none">🇮🇳</span>
                  <span>+91</span>
                </div>
                <Input type="tel" inputMode="numeric" className="flex-1 h-14 text-lg" placeholder="10-digit number" value={formData.businessPhone.startsWith("+91") ? formData.businessPhone.slice(3) : ""} onChange={(e) => setFormData({ ...formData, businessPhone: "+91" + e.target.value.replace(/\D/g, "").slice(0, 10) })} />
                {(() => {
                  const digits = formData.businessPhone.startsWith("+91") ? formData.businessPhone.slice(3) : "";
                  if (digits.length !== 10) return null;
                  const verified = user?.phoneNumber && "+91" + digits === user.phoneNumber;
                  return <BadgeCheck className={`h-6 w-6 shrink-0 ${verified ? "text-primary" : "text-muted-foreground"}`} />;
                })()}
              </div>
              <Button size="lg" className="w-full h-14 text-lg" onClick={nextStep} disabled={formData.businessPhone.length > 3 && formData.businessPhone.length < 13}>Next Step</Button>
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={skipStep}>Skip for now</Button>
            </div>
          </motion.div>
        );

      case "businessAddress":
        return (
          <motion.div key="businessAddress" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
            <div className="flex justify-center mb-4"><Logo size="md" /></div>
            <div className="space-y-2"><h2 className="text-2xl font-bold">Business Address</h2><p className="text-muted-foreground">Where is your shop located?</p></div>
            <div className="space-y-4">
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <textarea className="w-full min-h-[100px] pl-10 pt-2 bg-background border border-input rounded-md text-lg" placeholder="Enter full shop address" value={formData.businessAddress} onChange={(e) => setFormData({ ...formData, businessAddress: e.target.value })} />
              </div>
              <Button size="lg" className="w-full h-14 text-lg" onClick={nextStep}>Next Step</Button>
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={skipStep}>Skip for now</Button>
            </div>
          </motion.div>
        );

      case "businessWebsite":
        return (
          <motion.div key="businessWebsite" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
            <div className="flex justify-center mb-4"><Logo size="md" /></div>
            <div className="space-y-2"><h2 className="text-2xl font-bold">Have a website?</h2><p className="text-muted-foreground">Enter your domain (e.g. dailyhisaab.com)</p></div>
            <div className="space-y-4">
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input className="pl-10 h-14 text-lg" placeholder="yourbusiness.com" value={formData.businessWebsite} onChange={(e) => setFormData({ ...formData, businessWebsite: e.target.value })} />
              </div>
              <Button size="lg" className="w-full h-14 text-lg" onClick={nextStep}>Next Step</Button>
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={skipStep}>Skip for now</Button>
            </div>
          </motion.div>
        );

      case "referral":
        return (
          <motion.div key="referral" variants={stepVariants} initial="enter" animate="center" exit="exit" className="space-y-6 text-center">
            <div className="flex justify-center mb-4"><Logo size="md" /></div>
            <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center mb-6 mx-auto ring-4 ring-emerald-50/50">
              <Sparkles className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold">Were you invited?</h2>
            <p className="text-muted-foreground">Enter your friend's invite code to help them earn rewards!</p>
            <Input className="h-14 text-center text-2xl font-mono tracking-widest uppercase border-emerald-200" placeholder="OPTIONAL" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} />
            <Button size="lg" className="w-full h-14 text-lg bg-[#8B1A1A] hover:bg-[#631212] text-white" onClick={nextStep}>
              {referralCode ? "Apply Code & Finish" : "Complete Setup"}
            </Button>
            {!referralCode && <Button variant="ghost" className="w-full text-muted-foreground" onClick={skipStep}>I don't have a code</Button>}
          </motion.div>
        );

      case "completing":
        return (
          <div className="flex flex-col items-center justify-center space-y-4 py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-xl font-medium">Creating your profile...</p>
            <p className="text-muted-foreground">Setting up your Google Sheet sync...</p>
          </div>
        );
    }
  };

  const stepsList = ["businessName", "businessEmail", "businessPhone", "businessAddress", "businessWebsite", "referral"];

  return (
    <div className="min-h-screen bg-background flex flex-col p-6 max-w-lg mx-auto">
      <div className="flex-1 flex flex-col justify-center">
        <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
      </div>
      {step !== "welcome" && step !== "completing" && (
        <div className="py-6 flex justify-center gap-2">
          {stepsList.map((s, i) => (
            <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${stepsList.indexOf(step) >= i ? "w-8 bg-primary" : "w-2 bg-primary/20"}`} />
          ))}
        </div>
      )}
    </div>
  );
}